import Papa from 'papaparse'
import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Spin from '../../components/Spin'
import { apiGet } from '../../libs/apiHelper'
import { useAppContext } from '../../libs/context'
import { StatisticsGeneral } from './StatisticsGeneral'
import AdminCampaignCost from './AdminCampaignCost'
import { StatisticsRbm } from './StatisticsRbm'
import { CampaignSelector } from './components/CampaignSelector.jsx'

const DownloadSvg = ({ className = '' }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 56 60"
      fill="none"
      className={className}
    >
      <path
        d="M29.3838 41.2652C29.0285 41.6537 28.5265 41.875 28 41.875C27.4735 41.875 26.9715 41.6537 26.6163 41.2652L16.6162 30.3277C15.9175 29.5635 15.9706 28.3775 16.7348 27.6787C17.4991 26.98 18.6851 27.033 19.3838 27.7973L26.125 35.1705V7.5C26.125 6.46448 26.9645 5.625 28 5.625C29.0355 5.625 29.875 6.46448 29.875 7.5V35.1705L36.6163 27.7973C37.315 27.033 38.501 26.98 39.2653 27.6787C40.0295 28.3775 40.0825 29.5635 39.3838 30.3277L29.3838 41.2652Z"
        fill="currentColor"
      />
      <path
        d="M7.375 37.5C7.375 36.4645 6.53555 35.625 5.5 35.625C4.46448 35.625 3.625 36.4645 3.625 37.5V37.6373C3.62495 41.0563 3.6249 43.812 3.9163 45.9795C4.21885 48.2297 4.86608 50.1242 6.37088 51.629C7.87568 53.134 9.77035 53.7812 12.0206 54.0837C14.1881 54.375 16.9439 54.375 20.3628 54.375H35.6373C39.0563 54.375 41.812 54.375 43.9795 54.0837C46.2297 53.7812 48.1242 53.134 49.6292 51.629C51.134 50.1242 51.7812 48.2297 52.0837 45.9795C52.375 43.812 52.375 41.0563 52.375 37.6373V37.5C52.375 36.4645 51.5355 35.625 50.5 35.625C49.4645 35.625 48.625 36.4645 48.625 37.5C48.625 41.0885 48.621 43.5913 48.3673 45.4798C48.1205 47.3143 47.6695 48.2855 46.9775 48.9775C46.2855 49.6695 45.3143 50.1205 43.4798 50.3673C41.5913 50.621 39.0885 50.625 35.5 50.625H20.5C16.9115 50.625 14.4087 50.621 12.5203 50.3673C10.6859 50.1205 9.71443 49.6695 9.02253 48.9775C8.33063 48.2855 7.8795 47.3143 7.63288 45.4798C7.37898 43.5913 7.375 41.0885 7.375 37.5Z"
        fill="currentColor"
      />
    </svg>
  )
}

// Descarga por campaña: mismo patrón que /template-download. El backend genera
// el CSV, lo sube a S3 y devuelve una URL prefirmada. Evita el timeout de 30s
// y el límite de 6 MB del payload de Lambda que rompían las descargas grandes.
const handleDownloadDeliveries = async (campaignId) => {
  const res = await apiGet('/deliveries-info/' + campaignId)
  if (!res || !res.url) {
    throw new Error('No se recibió URL de descarga')
  }
  const tempLink = document.createElement('a')
  tempLink.href = res.url
  if (res.filename) tempLink.setAttribute('download', res.filename)
  document.body.appendChild(tempLink)
  tempLink.click()
  tempLink.remove()
}

const getCampaignStats = async (campaignId) => {
  let data
  try {
    // Usar el helper que automáticamente incluye el token de impersonación si está disponible
    data = await apiGet(`/campaign-stats/${campaignId}`)
    console.log('Statistics: getCampaignStats response:', {
      campaignId,
      hasData: !!data,
      hasRbmElements: !!data?.rbmElements,
      rbmElementsType: typeof data?.rbmElements,
      rbmElementsKeys: data?.rbmElements ? Object.keys(data.rbmElements) : [],
      rbmElementsCount: data?.rbmElements ? Object.keys(data.rbmElements).length : 0,
      fullData: data
    })
  } catch (error) {
    console.error('Statistics: Error getting campaign stats:', error)
  }
  return data
}

// Estadísticas agregadas de toda una template dentro de un rango de fechas.
const getTemplateStats = async (flow, from, to) => {
  let data
  try {
    const params = new URLSearchParams({ flow })
    if (from) params.append('from', from)
    if (to) params.append('to', to)
    data = await apiGet(`/template-stats?${params.toString()}`)
  } catch (error) {
    console.error('Statistics: Error getting template stats:', error)
  }
  return data
}

// Estadísticas globales del usuario: agrega TODAS las campañas del rango,
// cruzando flujos. Misma forma de respuesta que campaign/template-stats.
const getGlobalStats = async (from, to) => {
  let data
  try {
    const params = new URLSearchParams()
    if (from) params.append('from', from)
    if (to) params.append('to', to)
    const qs = params.toString()
    data = await apiGet(`/global-stats${qs ? `?${qs}` : ''}`)
  } catch (error) {
    console.error('Statistics: Error getting global stats:', error)
  }
  return data
}

// Descarga agregada de una template: el backend (template-download) genera el
// CSV de TODAS las campañas del rango, lo sube a S3 y devuelve una URL
// prefirmada. El navegador solo hace una petición y luego descarga la URL.
const handleTemplateDownload = async (flow, from, to, kind) => {
  // flow opcional: sin flow → descarga GLOBAL en bruto (todas las campañas).
  const params = new URLSearchParams({ kind })
  if (flow) params.append('flow', flow)
  if (from) params.append('from', from)
  if (to) params.append('to', to)
  const res = await apiGet(`/template-download?${params.toString()}`)
  if (!res || !res.url) {
    throw new Error('No se recibió URL de descarga')
  }
  const tempLink = document.createElement('a')
  tempLink.href = res.url
  if (res.filename) tempLink.setAttribute('download', res.filename)
  document.body.appendChild(tempLink)
  tempLink.click()
  tempLink.remove()
}

const toInputDate = (d) => {
  const month = `${d.getMonth() + 1}`.padStart(2, '0')
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${d.getFullYear()}-${month}-${day}`
}

// Rango por defecto al abrir las stats de una template: últimos 30 días.
const getDefaultTemplateRange = () => {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - 30)
  return { from: toInputDate(from), to: toInputDate(to) }
}

// Global arranca con un rango ligero (últimos 7 días) para que abra rápido: el
// agregado de 30 días de TODOS los flujos era lento. El usuario amplía con los
// presets o las fechas.
const getGlobalDefaultRange = () => {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - 7)
  return { from: toInputDate(from), to: toInputDate(to) }
}

const RANGE_PRESETS = [
  { key: 'today', label: 'Hoy' },
  { key: 'yesterday', label: 'Ayer' },
  { key: 'last7', label: '7 días' },
  { key: 'month', label: 'Este mes' },
  { key: 'all', label: 'Todo' }
]

// Devuelve {from,to} (yyyy-mm-dd) para un preset. 'all' → vacío = sin filtro.
const presetRange = (key) => {
  const today = new Date()
  const d = (dt) => toInputDate(dt)
  switch (key) {
    case 'today':
      return { from: d(today), to: d(today) }
    case 'yesterday': {
      const y = new Date()
      y.setDate(y.getDate() - 1)
      return { from: d(y), to: d(y) }
    }
    case 'last7': {
      const f = new Date()
      f.setDate(f.getDate() - 6)
      return { from: d(f), to: d(today) }
    }
    case 'last30': {
      const f = new Date()
      f.setDate(f.getDate() - 29)
      return { from: d(f), to: d(today) }
    }
    case 'month':
      return {
        from: d(new Date(today.getFullYear(), today.getMonth(), 1)),
        to: d(today)
      }
    case 'all':
      return { from: '', to: '' }
    default:
      return { from: d(today), to: d(today) }
  }
}

const addClientInfo = async (campaigns) => {
  let users = []
  try {
    // Usar el helper que automáticamente incluye el token de impersonación si está disponible
    users = await apiGet('/users')
  } catch (err) {
    console.log('error con los clientes')
  }

  campaigns.forEach((campaign) => {
    let client = users.find((user) => user.userId === campaign.userId)
    if (client) {
      campaign.clientInfo = client
    }
  })

  return campaigns
}

// Helper function to check if there are any statistics available
const hasStatistics = (campaignInfo) => {
  if (!campaignInfo) return false
  
  // Check SMS stats
  const hasSmsStats = (campaignInfo?.sms?.sent ?? 0) > 0 || (campaignInfo?.sms?.delivered ?? 0) > 0
  
  // Check RBM stats
  const hasRbmStats = 
    (campaignInfo?.rbm?.sent ?? 0) > 0 || 
    (campaignInfo?.rbm?.delivered ?? 0) > 0 || 
    (campaignInfo?.rbm?.displayed ?? 0) > 0
  
  // Check date stats
  const hasDateStats = campaignInfo?.dateStats && 
    typeof campaignInfo.dateStats === 'object' &&
    Object.keys(campaignInfo.dateStats).length > 0 &&
    Object.values(campaignInfo.dateStats).some(stats => 
      stats && typeof stats === 'object' && Object.keys(stats).length > 0
    )
  
  // Check RBM elements
  const hasRbmElements = campaignInfo?.rbmElements && 
    typeof campaignInfo.rbmElements === 'object' &&
    Object.keys(campaignInfo.rbmElements).length > 0
  
  return hasSmsStats || hasRbmStats || hasDateStats || hasRbmElements
}

// Helper function to check if there are RBM statistics
const hasRbmStatistics = (campaignInfo) => {
  if (!campaignInfo) return false
  
  const hasRbmStats = 
    (campaignInfo?.rbm?.sent ?? 0) > 0 || 
    (campaignInfo?.rbm?.delivered ?? 0) > 0 || 
    (campaignInfo?.rbm?.displayed ?? 0) > 0
  
  const hasRbmElements = campaignInfo?.rbmElements && 
    typeof campaignInfo.rbmElements === 'object' &&
    Object.keys(campaignInfo.rbmElements).length > 0
  
  return hasRbmStats || hasRbmElements
}

export const Statistics = () => {
  const history = useHistory()
  const location = useLocation()
  const [viewType, setViewType] = useState('general')
  const [view, setView] = useState(() => <StatisticsGeneral />)
  const [campaigns, setCampaigns] = useState([])
  const [campaignInfo, setCampaignInfo] = useState()
  const [loadingCampaigns, setLoadingCampaigns] = useState(true)
  const { userInfo } = useAppContext()
  const campaignIdFromUrl = new URLSearchParams(location.search).get(
    'campaignId'
  )
  const templateFromUrl = new URLSearchParams(location.search).get('template')
  const globalFromUrl = new URLSearchParams(location.search).get('global')
  const isDirectCampaignView = Boolean(campaignIdFromUrl)
  const isTemplateView = Boolean(templateFromUrl)
  const isGlobalView = Boolean(globalFromUrl)
  const [templateRange, setTemplateRange] = useState(
    isGlobalView ? getGlobalDefaultRange() : getDefaultTemplateRange()
  )
  // Estado de la descarga agregada (deliveries/answers) en modo template.
  const [downloading, setDownloading] = useState(null) // 'deliveries' | 'answers' | null
  // Guard anti-race: la carga por defecto (30 días, pesada) puede terminar
  // DESPUÉS de un filtro más nuevo y sobrescribirlo (se veía "19 campañas" al
  // filtrar por un día). Cada carga coge un nº de secuencia y solo aplica su
  // resultado si sigue siendo la última pedida.
  const reqSeq = useRef(0)

  const runAggregatedDownload = async (kind) => {
    // Vale para template (con flow) y global (sin flow → todo en bruto).
    if (downloading || (!isTemplateView && !isGlobalView)) return
    setDownloading(kind)
    try {
      await handleTemplateDownload(
        templateFromUrl,
        templateRange.from,
        templateRange.to,
        kind
      )
    } catch (error) {
      console.error('Statistics: Error en descarga agregada:', error)
      alert('No se pudo generar la descarga. Inténtalo de nuevo.')
    } finally {
      setDownloading(null)
    }
  }

  const normalizeCampaignData = (campaignData, fallbackCampaignId) => {
    // Priority: name > flow (template name) > campaignName > undefined (don't show ID)
    let name = campaignData?.name || campaignData?.campaignName
    
    // If no name, extract template name from flow (e.g., "caixarecobros-checkReason" -> "caixarecobros")
    if (!name && campaignData?.flow) {
      const flowParts = campaignData.flow.split('-')
      name = flowParts[0] || campaignData.flow
    }
    
    // Preserve all data including rbmElements
    const normalized = {
      ...campaignData,
      name,
      campaignId: campaignData?.campaignId || fallbackCampaignId
    }
    
    // Explicitly preserve rbmElements if it exists
    if (campaignData?.rbmElements) {
      normalized.rbmElements = campaignData.rbmElements
    }
    
    return normalized
  }

  const setCampaignView = (campaignData, fallbackCampaignId) => {
    console.log('Statistics: setCampaignView called with:', {
      campaignData,
      fallbackCampaignId,
      hasRbmElements: !!campaignData?.rbmElements,
      rbmElements: campaignData?.rbmElements,
      rbmElementsType: typeof campaignData?.rbmElements,
      rbmElementsKeys: campaignData?.rbmElements ? Object.keys(campaignData.rbmElements) : [],
      rbmElementsCount: campaignData?.rbmElements ? Object.keys(campaignData.rbmElements).length : 0,
      fullCampaignDataKeys: Object.keys(campaignData || {})
    })
    
    const normalizedCampaign = normalizeCampaignData(
      campaignData,
      fallbackCampaignId
    )
    
    console.log('Statistics: normalizedCampaign:', {
      name: normalizedCampaign.name,
      flow: normalizedCampaign.flow,
      campaignName: normalizedCampaign.campaignName,
      campaignId: normalizedCampaign.campaignId,
      hasRbmElements: !!normalizedCampaign.rbmElements,
      rbmElementsKeys: normalizedCampaign.rbmElements ? Object.keys(normalizedCampaign.rbmElements) : [],
      rbmElementsCount: normalizedCampaign.rbmElements ? Object.keys(normalizedCampaign.rbmElements).length : 0,
      allKeys: Object.keys(normalizedCampaign || {})
    })
    
    // Ensure rbmElements is preserved
    if (campaignData?.rbmElements && !normalizedCampaign.rbmElements) {
      normalizedCampaign.rbmElements = campaignData.rbmElements
      console.log('Statistics: Restored rbmElements from campaignData')
    }
    
    const generalView = <StatisticsGeneral campaignInfo={normalizedCampaign} />
    setView(() => generalView)
    setViewType('general')
    setCampaignInfo(normalizedCampaign)
  }

  async function getCampaigns() {
    let campaignsList = []
    try {
      // Usar el helper que automáticamente incluye el token de impersonación si está disponible
      const response = await apiGet('/campaigns')

      // Adaptar a nueva estructura de respuesta paginada
      if (Array.isArray(response)) {
        // Formato antiguo (compatibilidad)
        campaignsList = response
      } else if (response.campaigns) {
        // Nuevo formato con paginación - solo tomar la primera página
        campaignsList = response.campaigns
      } else {
        campaignsList = []
      }
    } catch (error) {
      console.error(error)
    }

    if (userInfo.isAdmin && campaignsList && Array.isArray(campaignsList)) {
      campaignsList = await addClientInfo(campaignsList)
    }

    if (campaignsList && campaignsList.length) {
      campaignsList
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
        .reverse()
      setCampaigns(campaignsList)
    } else {
      alert('Campaign without data yet.')
    }
    setLoadingCampaigns(false)
  }

  const loadTemplateStats = async (flow, range) => {
    const seq = ++reqSeq.current
    setLoadingCampaigns(true)
    const data = await getTemplateStats(flow, range.from, range.to)
    if (seq !== reqSeq.current) return // respuesta obsoleta: llegó una más nueva
    if (!data) {
      console.error('Failed to load template stats')
      setLoadingCampaigns(false)
      return
    }
    // La respuesta tiene la misma forma que campaign-stats (name = flow), así
    // que reutilizamos la misma vista sin cambios.
    setCampaignView({ ...data, name: flow }, undefined)
    setLoadingCampaigns(false)
  }

  const loadGlobalStats = async (range) => {
    const seq = ++reqSeq.current
    setLoadingCampaigns(true)
    const data = await getGlobalStats(range.from, range.to)
    if (seq !== reqSeq.current) return // respuesta obsoleta: llegó una más nueva
    if (!data) {
      console.error('Failed to load global stats')
      setLoadingCampaigns(false)
      return
    }
    setCampaignView({ ...data, name: 'Estadísticas globales' }, undefined)
    setLoadingCampaigns(false)
  }

  // Aplica un rango directamente (presets). Un preset siempre es intencional;
  // 'Todo' llega con from/to vacíos → el backend devuelve el histórico.
  const applyRange = (range) => {
    setTemplateRange(range)
    if (isGlobalView) {
      loadGlobalStats(range)
    } else if (isTemplateView) {
      loadTemplateStats(templateFromUrl, range)
    }
  }

  // Auto-aplica al cambiar una fecha, pero solo si el rango es válido (ambas
  // fechas completas y from <= to). El guard anti-race evita solapes.
  const maybeAutoApply = (next) => {
    setTemplateRange(next)
    const { from, to } = next
    const ok =
      from &&
      to &&
      !isNaN(new Date(from).getTime()) &&
      !isNaN(new Date(to).getTime()) &&
      new Date(from) <= new Date(to)
    if (!ok) return
    if (isGlobalView) {
      loadGlobalStats(next)
    } else if (isTemplateView) {
      loadTemplateStats(templateFromUrl, next)
    }
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const campaignIdFromUrl = searchParams.get('campaignId')
    const templateNameFromUrl = searchParams.get('template')
    const globalParam = searchParams.get('global')

    if (globalParam) {
      loadGlobalStats(templateRange)
      return
    }

    if (templateNameFromUrl) {
      loadTemplateStats(templateNameFromUrl, templateRange)
      return
    }

    if (campaignIdFromUrl) {
      const loadCampaignStats = async () => {
        const seq = ++reqSeq.current
        setLoadingCampaigns(true)
        const campaignData = await getCampaignStats(campaignIdFromUrl)
        if (seq !== reqSeq.current) return // respuesta obsoleta
        if (!campaignData) {
          console.error('Failed to load campaign stats')
          setLoadingCampaigns(false)
          return
        }
        
        console.log('Statistics: Direct load campaign data:', {
          campaignId: campaignIdFromUrl,
          name: campaignData.name,
          flow: campaignData.flow,
          campaignName: campaignData.campaignName,
          hasRbmElements: !!campaignData.rbmElements,
          rbmElementsKeys: campaignData.rbmElements ? Object.keys(campaignData.rbmElements) : [],
          rbmElementsCount: campaignData.rbmElements ? Object.keys(campaignData.rbmElements).length : 0
        })
        
        setCampaignView(campaignData, campaignIdFromUrl)
        setLoadingCampaigns(false)
      }

      loadCampaignStats()
      return
    }

    getCampaigns()
  }, [location.search])

  const handleCampaignChange = async (campaign) => {
    console.log('Statistics: Loading campaign:', campaign.campaignId)

    const campaignData = await getCampaignStats(campaign.campaignId)
    if (!campaignData) {
      console.error('Failed to load campaign stats')
      return
    }
    
    console.log('Statistics: Campaign data loaded:', {
      name: campaignData.name,
      hasRbmElements: !!campaignData.rbmElements,
      rbmElementsKeys: campaignData.rbmElements ? Object.keys(campaignData.rbmElements) : [],
      rbmElementsCount: campaignData.rbmElements ? Object.keys(campaignData.rbmElements).length : 0
    })
    
    setCampaignView(
      {
        ...campaignData,
        name: campaign.flow || campaignData.name
      },
      campaign.campaignId
    )
  }


  return (
    <>
      {loadingCampaigns ? (
        <div className="flex flex-col items-center justify-center my-16">
          <h2 className="text-2xl font-semibold text-grey-blue mb-8">
            Loading{' '}
            {isDirectCampaignView || isTemplateView ? 'statistics' : 'campaigns'}{' '}
            ...
          </h2>
          <Spin type="lightBg" />
        </div>
      ) : (
        <>
          {!campaignInfo ? (
            <CampaignSelector
              setCampaign={handleCampaignChange}
              campaigns={campaigns}
            />
          ) : (
            <div className="min-w-[1000px] pb-24">
              {/* Floating Action Bar - Similar to Templates/Campaigns */}
              {hasStatistics(campaignInfo) && (
                <div className="fixed bottom-6 z-50 left-[calc(50%+128px)] transform -translate-x-1/2">
                  <div className="flex items-center gap-3 px-5 py-3.5 rounded-2xl bg-white/90 backdrop-blur-2xl border-2 border-white/40 shadow-2xl ring-2 ring-white/30">
                    {/* View Toggle */}
                    <div className="flex items-center bg-gray-100/80 rounded-full p-1">
                      <button
                        className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                          viewType === 'general'
                            ? 'bg-white text-indigo-600 shadow-md'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                        onClick={() => {
                          setView(() => (
                            <StatisticsGeneral campaignInfo={campaignInfo} />
                          ))
                          setViewType('general')
                        }}
                      >
                        All
                      </button>
                      {hasRbmStatistics(campaignInfo) && (
                        <button
                          className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                            viewType === 'rbm'
                              ? 'bg-white text-indigo-600 shadow-md'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                          onClick={() => {
                            setView(() => (
                              <StatisticsRbm
                                rbmElements={campaignInfo?.rbmElements || {}}
                                campaignInfo={campaignInfo}
                              />
                            ))
                            setViewType('rbm')
                          }}
                        >
                          RBM
                        </button>
                      )}
                    </div>

                    {/* Divider */}
                    <div className="w-px h-8 bg-gray-300/50" />

                    {/* Descarga unificada (deliveries + answers, lossless).
                        En global va sin flow → descarga todo en bruto. */}
                    <button
                      disabled={downloading !== null}
                      className="inline-flex gap-2 items-center justify-center px-4 py-2 rounded-full text-sm font-semibold text-gray-700 bg-white/95 border-2 border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-300 hover:scale-105 active:scale-95 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                      onClick={() =>
                        campaignInfo.campaignIds
                          ? runAggregatedDownload('deliveries')
                          : handleDownloadDeliveries(campaignInfo.campaignId)
                      }
                    >
                      {downloading !== null ? (
                        <>
                          <svg
                            className="animate-spin w-4 h-4 text-gray-700"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                          <span>Generando…</span>
                        </>
                      ) : (
                        <>
                          <DownloadSvg className="text-gray-700 w-4 h-4" />
                          <span>Descargar CSV</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
              
              {(isTemplateView || isGlobalView) && (
                <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-2 bg-white rounded-xl border-2 border-indigo-100 shadow-sm px-5 py-3">
                  {/* Título: empuja presets + fechas a la derecha en una sola fila. */}
                  <div className="mr-auto">
                    <div className="text-base font-bold text-grey-blue leading-tight">
                      {isGlobalView ? 'Estadísticas globales' : templateFromUrl}
                    </div>
                    <div className="text-xs text-gray-500">
                      {campaignInfo?.campaignCount ?? 0}{' '}
                      {campaignInfo?.campaignCount === 1
                        ? 'campaña'
                        : 'campañas'}{' '}
                      en el rango
                    </div>
                  </div>
                  {/* Presets rápidos: un clic aplica el rango al instante. */}
                  <div className="flex items-center gap-1.5">
                    {RANGE_PRESETS.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => applyRange(presetRange(p.key))}
                        className="px-2.5 py-1.5 rounded-full text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100 transition-colors"
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  {/* Fechas manuales compactas (auto-aplican al completar el rango). */}
                  <div className="flex items-center gap-1.5">
                    <input
                      type="date"
                      value={templateRange.from}
                      onChange={(e) =>
                        maybeAutoApply({ ...templateRange, from: e.target.value })
                      }
                      className="border-2 border-indigo-300 focus:border-indigo-600 focus:ring-2 focus:ring-indigo-200 rounded-lg px-2.5 py-1.5 text-sm bg-white shadow-sm"
                    />
                    <span className="text-gray-400">–</span>
                    <input
                      type="date"
                      value={templateRange.to}
                      onChange={(e) =>
                        maybeAutoApply({ ...templateRange, to: e.target.value })
                      }
                      className="border-2 border-indigo-300 focus:border-indigo-600 focus:ring-2 focus:ring-indigo-200 rounded-lg px-2.5 py-1.5 text-sm bg-white shadow-sm"
                    />
                  </div>
                </div>
              )}

              {campaignInfo ? view : null}

              {/* Coste Orange: solo admin real, nunca al suplantar a un cliente */}
              {userInfo.isAdmin &&
                !userInfo.impersonatedUserId &&
                campaignInfo &&
                campaignInfo.campaignId &&
                !campaignInfo.isGlobal &&
                !isTemplateView && (
                  <AdminCampaignCost campaignId={campaignInfo.campaignId} />
                )}
            </div>
          )}
        </>
      )}
    </>
  )
}
